import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Activity, Clock, Calendar, FileText, LogIn, ChevronRight } from 'lucide-react';
import useUsageAnalytics from '../../hooks/useUsageAnalytics';

const formatNumber = (value) => Number(value || 0).toLocaleString();

const ICON_STYLES = {
  blue: { bg: 'bg-blue-100', fg: 'text-blue-800' },
  amber: { bg: 'bg-amber-100', fg: 'text-amber-600' },
  red: { bg: 'bg-red-100', fg: 'text-red-600' },
  teal: { bg: 'bg-teal-100', fg: 'text-teal-700' },
};

const Tile = ({ icon: Icon, color, value, label }) => {
  const style = ICON_STYLES[color] || ICON_STYLES.blue;
  return (
    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
      <div className={`w-9 h-9 sm:w-10 sm:h-10 ${style.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${style.fg}`} aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="text-base sm:text-xl font-bold text-gray-900 leading-tight truncate">{value}</p>
        <p className="text-xs text-gray-500 truncate">{label}</p>
      </div>
    </div>
  );
};

/**
 * Compact "at a glance" usage summary for the Admin Dashboard.
 * Shows the last 30 days of headline activity and links to the full
 * Platform Usage & Adoption report at /admin/usage.
 */
const UsageAnalyticsSummary = () => {
  const { data, isLoading, isFetching } = useUsageAnalytics(30);
  const totals = data?.totals || {};
  const clockTotal = (totals.clock_in || 0) + (totals.clock_out || 0);

  const generatedAt = data?.generated_at ? new Date(data.generated_at) : null;
  const freshnessLabel = isFetching
    ? 'Refreshing…'
    : generatedAt
    ? `Updated ${formatDistanceToNow(generatedAt, { addSuffix: true })}`
    : null;

  return (
    <section aria-label="Platform usage summary">
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <Activity className="w-5 h-5 text-red-600 flex-shrink-0" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              Platform Usage &amp; Adoption
            </h3>
            <span className="hidden sm:inline text-xs text-gray-400 flex-shrink-0">· last 30 days</span>
            {freshnessLabel && (
              <span
                className="hidden md:inline text-xs text-gray-400 flex-shrink-0"
                title={generatedAt ? generatedAt.toLocaleString() : undefined}
              >
                · {freshnessLabel}
              </span>
            )}
          </div>
          <Link
            to="/admin/usage"
            className="inline-flex items-center gap-1 text-xs sm:text-sm font-medium text-red-700 hover:text-red-900 whitespace-nowrap flex-shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 rounded"
            aria-label="View full usage report"
          >
            <span className="hidden sm:inline">View full report</span>
            <span className="sm:hidden">View</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="h-5 bg-gray-200 rounded w-12 mb-1" />
                  <div className="h-3 bg-gray-200 rounded w-16" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Tile icon={Clock} color="blue" value={formatNumber(clockTotal)} label="Clock Events" />
            <Tile icon={Calendar} color="amber" value={formatNumber(totals.leave_submitted)} label="Leave Requests" />
            <Tile icon={FileText} color="red" value={formatNumber(totals.documents_uploaded)} label="Documents" />
            <Tile icon={LogIn} color="teal" value={formatNumber(totals.logins)} label="Logins" />
          </div>
        )}
      </div>
    </section>
  );
};

export default UsageAnalyticsSummary;
